import React, { memo, useMemo } from "react"
import ReactDOM, { hydrate, render } from "react-dom"
import { ApolloProvider } from "react-apollo"
import { BrowserRouter as Router, Route, Redirect, Switch } from "react-router-dom"
import { CookiesProvider } from "react-cookie"
import { Normalize } from "styled-normalize"
import { ThemeProvider } from "styled-components"

import Dashboard from "./dashboard"
import ErrorBoundary from "components/error-boundary"
import NavigationLayout from "layouts/navigation"
import { UserRoute } from "shared/routes/user-route"
import { GuestRoute } from "shared/routes/guest-route"
import { ErrorGuardRoute } from "shared/routes/error-guard-route"
import { LoadingGuardRoute } from "shared/routes/loading-guard-route"
import { lazy } from "shared/lazy"

import { PromobarProvider } from "shared/context/promobar"
import { ProductsProvider, useProductsState } from "shared/context/products"
import { CartProvider, useUpdateUserCartState } from "shared/cart/context"
import { MutationLoadingStatusProvider } from "shared/context/mutation-loading-status"
import { useRootQuery, CurrentUser, CurrentOffer } from "graphql/smart-queries/user"

import useMarketingCookies from "hooks/useMarketingCookies"
import useUpdateProducts from "hooks/useUpdateProducts"

import appConfig from "config"
import WindowDebug from "shared/WindowDebug"
import createClient from "shared/apollo/client"
import { configureSentry, setSentryScope } from "config/sentry"
import AnalyticsDispatcher from "shared/AnalyticsDispatcher"
import { GlobalStyles, ReactWidgets } from "theme/global-styles"
import { CoveTheme } from "theme"
import { showPDP } from "./products/helpers/excluded-product-pdps"
import ReturningUser from "shared/returning-user"

import { NoMatchPage } from "pages/no-match-page"

const Careers = lazy(() => import("./careers"))
const CareersFull = lazy(() => import("./careers/components/careers-full"))
const Contact = lazy(() => import("./contact"))
const Cove101 = lazy(() => import("./cove-101"))
const DrugFacts = lazy(() => import("./drug-facts"))
const GetStartedFlow = lazy(() => import("./get-started"))
const Homepage = lazy(() => import("./marketing-pages/homepage"))
const Learn = lazy(() => import("./learn"))
const LegalsFull = lazy(() => import("./legals/components/legals-full"))
const LegalsHome = lazy(() => import("./legals"))
const LoggedOut = lazy(() => import("./users"))
const MarketingGetStarted = lazy(() => import("./marketing-pages/purchase"))
const OurOffering = lazy(() => import("./our-offering"))
const Press = lazy(() => import("./press"))
const Product = lazy(() => import("./products"))
const PurchaseFlow = lazy(() => import("./purchase"))
const Referral = lazy(() => import("./referral"))
const Reviews = lazy(() => import("./reviews"))
const RenewalFlow = lazy(() => import("./renewal"))
const RetakeMedia = lazy(() => import("./retake-media"))

export const apolloClient = createClient()
configureSentry()

const WrappedRoot: React.FC = () => {
  const { data, loading, error } = useRootQuery()
  const { productGroups } = useProductsState()

  useMarketingCookies()
  useUpdateProducts()
  useUpdateUserCartState(data?.user)

  let currentOffer: Maybe<CurrentOffer>, user: Maybe<CurrentUser>

  if (!loading && data) {
    user = data.user
    currentOffer = data.currentOffer
  }

  if (data?.user?.email && data?.user?.id) setSentryScope(data.user)

  const showOfferBanner = !user || user.subscriptions.length === 0
  const topBannerText = showOfferBanner && currentOffer ? currentOffer.topBannerText : null

  /* due to legal complications, we are currently filtering out the cambia PDP
    https://github.com/ThirtyMadison/cove/issues/2028 */
  const productPaths = useMemo(
    () =>
      productGroups.filter(pg => pg.products.filter(p => showPDP(p)).length > 0).map(pg => `/products/${pg.drugType}`),
    []
  )

  return (
    <MutationLoadingStatusProvider>
      <NavigationLayout topBannerText={topBannerText} currentUser={user}>
        <Switch>
          <Route exact path="/" component={Homepage} />
          <Route exact path="/purchase" component={MarketingGetStarted} />
          <Route exact path="/cove-101" component={Cove101} />
          <Route exact path="/migraines-101" component={() => <Redirect to="/cove-101" />} />
          <Route exact path="/" component={Homepage} />
          {productPaths.map(path => (
            <Route path={path} key={path} exact component={Product} />
          ))}
          <Route path="/drug-facts" component={DrugFacts} />
          <Route path="/press" component={Press} />
          <Route path="/our-reviews" exact component={Reviews} />
          <Route path="/learn/" component={Learn} />
          <Route path="/legals" exact component={LegalsHome} />
          <Route path="/legals/:path" exact component={LegalsFull} />
          <Route path="/contact" exact component={Contact} />
          <Route path="/careers" exact component={Careers} />
          <Route path="/careers/:greenhouseId" exact component={CareersFull} />

          {/* Pages above this route are rendered even if root graphql query is loading. Loading state can be handled on page-level. Also we can assert that root query loading is false for all pages below. */}
          {loading && <LoadingGuardRoute />}
          {/* Pages above this route are rendered even if graphql call returns an error. Errors must be handled on page-level. */}
          {error && <ErrorGuardRoute error={error} />}

          <Route path="/our-offering" exact component={OurOffering} />
          <Route path="/get-started" component={GetStartedFlow} />
          <Route path="/purchase" component={PurchaseFlow} />
          <UserRoute path="/renewal" component={RenewalFlow} />
          <UserRoute path="/dashboard" component={Dashboard} />
          <UserRoute path="/retake-media" component={RetakeMedia} />
          <GuestRoute path="/users" component={LoggedOut} />
          <Route path="/referral" exact component={Referral} />
          <Route exact path="/start" component={ReturningUser} />

          <Route component={NoMatchPage} />
        </Switch>
      </NavigationLayout>
      <WindowDebug />
    </MutationLoadingStatusProvider>
  )
}

const Root = memo(WrappedRoot)

const App = () => (
  <ThemeProvider theme={CoveTheme}>
    <>
      <Normalize />
      <GlobalStyles />
      <ReactWidgets />
      <ErrorBoundary>
        <ApolloProvider client={apolloClient}>
          <CookiesProvider>
            <Router>
              <AnalyticsDispatcher>
                <ProductsProvider>
                  <CartProvider>
                    <PromobarProvider>
                      <Root />
                    </PromobarProvider>
                  </CartProvider>
                </ProductsProvider>
              </AnalyticsDispatcher>
            </Router>
          </CookiesProvider>
        </ApolloProvider>
      </ErrorBoundary>
    </>
  </ThemeProvider>
)

/*
  !Make sure to only run the axe a11y reporting in development environment
*/
if (appConfig.app.a11y === "true" && appConfig.app.env === "dev") {
  import("react-axe")
    .then(({ default: axe }) => axe(React, ReactDOM, 1000))
    .catch(err => console.log(err.message, "React axe loading error"))
}

const rootElement = document.getElementById("root")

if (rootElement!.hasChildNodes()) {
  hydrate(<App />, rootElement)
} else {
  render(<App />, rootElement)
}
